/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Heading, Icon, Text, ButtonGroup, Button, useFlags } from 'sarsaparilla';
import { SITE_PASS_BANNERS } from '../constants';
import { showSitePassBanner } from '../utils/launchDarklyUtils';

function LEGACY_SitePassBanner({ facilityId = '', onToggleShowPasses = () => {} }) {
    const { facilitiesUsingSitePassIntegration } = useFlags();
    const sitePassBannerData = useMemo(() => {
        return SITE_PASS_BANNERS?.[facilityId] ?? null;
    }, [facilityId]);

    const shouldShowSitePassBanner = useMemo(() => {
        return showSitePassBanner(facilityId, facilitiesUsingSitePassIntegration);
    }, [facilitiesUsingSitePassIntegration, facilityId]);

    const handleBookReservation = useCallback(() => {
        window.location.href = `/timed-entry/${sitePassBannerData?.timedEntryFacilityId}`;
    }, [sitePassBannerData?.timedEntryFacilityId]);

    const renderAreasWithReservation = () => {
        if (sitePassBannerData?.areasWithReservation?.length) {
            return (
                <div className="LEGACY_site-pass-banner__list-container LEGACY_site-pass-banner__info-content-item">
                    <Text
                        id="LEGACY_site-pass-banner-list-title"
                        className="LEGACY_site-pass-banner__list-title"
                        aria-hidden="true"
                    >
                        {sitePassBannerData?.areaListTitle ||
                            'Areas that require a reservation:'}
                    </Text>
                    <ul
                        className="LEGACY_site-pass-banner__list"
                        aria-labelledby="LEGACY_site-pass-banner-list-title"
                    >
                        {sitePassBannerData?.areasWithReservation?.map(
                            (areaWithReservation, key) => (
                                <li
                                    className="LEGACY_site-pass-banner__list-item"
                                    key={key}
                                    aria-label={`${areaWithReservation?.name} ${areaWithReservation?.dates}`}
                                >
                                    <div
                                        className="LEGACY_site-pass-banner__list-content"
                                        aria-hidden="true"
                                    >
                                        <Text
                                            id="LEGACY_site-pass-banner__list-name"
                                            className="LEGACY_site-pass-banner__list-name"
                                            tag="span"
                                        >
                                            {areaWithReservation?.name}
                                        </Text>
                                        {areaWithReservation?.dates && (
                                            <>
                                                <Icon
                                                    iconName="calendar"
                                                    className="LEGACY_site-pass-banner__list-date-icon"
                                                    size="sm"
                                                />
                                                <Text
                                                    id="LEGACY_site-pass-banner__list-date-text"
                                                    className="LEGACY_site-pass-banner__list-date-text"
                                                    fontStyle="italic"
                                                    tag="span"
                                                    size="sm"
                                                >
                                                    {areaWithReservation?.dates}
                                                </Text>
                                            </>
                                        )}
                                    </div>
                                </li>
                            )
                        )}
                    </ul>
                </div>
            );
        }

        return null;
    };

    const renderButtons = () => {
        if (
            !!sitePassBannerData?.hideBookReservationButton &&
            !!sitePassBannerData?.hideShowAvailablePassesButton
        ) {
            return null;
        }

        return (
            <ButtonGroup className="LEGACY_site-pass-banner__buttons-container" isStacked>
                {!sitePassBannerData?.hideBookReservationButton && (
                    <Button
                        id="LEGACY_site-pass-banner-button-reservation"
                        className="LEGACY_site-pass-banner__button-reservation"
                        appearance="solid-white"
                        clickTagAction={`Location ID - ${facilityId}`}
                        clickTagCategory="Site Pass"
                        clickTagLabel="Pass Upsell Promo - Book Reservation Now Button"
                        iconBeforeElement={
                            <Icon
                                iconName="car"
                                id="LEGACY_site-pass-banner-button-reservation-icon"
                                className="LEGACY_site-pass-banner__button-icon"
                            />
                        }
                        onClick={handleBookReservation}
                    >
                        Book Reservation Now
                    </Button>
                )}
                {!sitePassBannerData?.hideShowAvailablePassesButton && (
                    <Button
                        id="LEGACY_site-pass-banner-button-show-passes"
                        className="LEGACY_site-pass-banner__button-show-passes"
                        appearance="tertiary-white"
                        clickTagAction={`Location ID - ${facilityId}`}
                        clickTagCategory="Site Pass"
                        clickTagLabel="Pass Upsell Promo - Show Available Passes Button"
                        iconBeforeElement={
                            <Icon
                                iconName="card"
                                id="LEGACY_site-pass-banner-button-show-passes-icon"
                                className="LEGACY_site-pass-banner__button-icon"
                            />
                        }
                        onClick={onToggleShowPasses}
                    >
                        Show Available Passes
                    </Button>
                )}
            </ButtonGroup>
        );
    };

    if (!sitePassBannerData || !shouldShowSitePassBanner) {
        return null;
    }

    return (
        <div className="LEGACY_site-pass-banner">
            <div className="LEGACY_site-pass-banner__img-background" />
            <div className="LEGACY_site-pass-banner__content">
                <div className="LEGACY_site-pass-banner__left-content">
                    <div className="LEGACY_site-pass-banner__content-header">
                        <Icon
                            iconName="info"
                            className="LEGACY_site-pass-banner__content-header-icon"
                            size="sm"
                        />
                        <Heading
                            id="LEGACY_site-pass-banner-title"
                            className="LEGACY_site-pass-banner__title"
                            headingLevel={2}
                            appearance="h4"
                        >
                            {sitePassBannerData?.title}
                        </Heading>
                    </div>
                    <div className="LEGACY_site-pass-banner__content-body">
                        <Heading
                            id="LEGACY_site-pass-banner-description"
                            className="LEGACY_site-pass-banner__description"
                            headingLevel={2}
                            appearance="h6"
                        >
                            {sitePassBannerData?.description}
                        </Heading>
                        <div className="LEGACY_site-pass-banner__info-content">
                            {renderAreasWithReservation()}
                        </div>
                    </div>
                </div>
                <div className="LEGACY_site-pass-banner__right-content">
                    {renderButtons()}
                </div>
            </div>
        </div>
    );
}

LEGACY_SitePassBanner.propTypes = {
    facilityId: PropTypes.string,
    onToggleShowPasses: PropTypes.func,
};

export default LEGACY_SitePassBanner;
